import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, FormControl, FormGroup} from '@angular/forms';
import {Field} from '@app/interfaces/entities/field';
import {merge, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

export interface SegmentDefinition {
  field: string;
  label: string;
  type: 'select' | 'boolean';
  operators: { value: string; label: string }[];
  operatorMetadata?: Record<string, { inputType: 'select' | 'multiselect'; label: string }>;
  options: { value: any; label: string; code?: string }[];
}

export interface SegmentConditionValue {
  field: string;
  operator: string;
  value: any;
}

export interface SegmentationValue {
  logicalOperator: string;
  conditions: SegmentConditionValue[];
}

@Component({
  selector: 'app-form-input-segmentation',
  templateUrl: './form-input-segmentation.component.html',
  styleUrl: './form-input-segmentation.component.scss',
  standalone: false
})
export class FormInputSegmentationComponent implements OnInit, OnDestroy {

  @Input() field: Field<SegmentationValue>;
  @Input() control: AbstractControl;

  readonly logicalOperatorControl = new FormControl<string>('AND');
  conditions: FormGroup[] = [];

  readonly multiselectPlaceholder = $localize`Select values`;
  readonly multiselectSettings = {
    singleSelection: false,
    defaultOpen: false,
    idField: 'value',
    textField: 'label',
    enableCheckAll: false,
    itemsShowLimit: 2,
    allowSearchFilter: true
  };

  private destroy$ = new Subject<void>();
  private conditionDestroys: Subject<void>[] = [];

  get segments(): SegmentDefinition[] {
    return this.field.config['segments'] ?? [];
  }

  get logicalOperators(): string[] {
    return this.field.config['logicalOperators'] ?? ['AND', 'OR'];
  }

  ngOnInit() {
    const existing = this.control.value as SegmentationValue | null;
    if (existing?.conditions?.length) {
      this.logicalOperatorControl.setValue(existing.logicalOperator ?? 'AND');
      existing.conditions.forEach(conditionValue => this.addCondition(conditionValue));
    } else {
      this.addCondition();
    }

    this.logicalOperatorControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.writeToControl());
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.conditionDestroys.forEach(subject => {
      subject.next();
      subject.complete();
    });
  }

  addCondition(initial?: SegmentConditionValue) {
    const fieldControl = new FormControl<string>(initial?.field ?? '');
    const operatorControl = new FormControl<string>(initial?.operator ?? '');
    const valueControl = new FormControl<any>(this.getInitialValue(initial));

    const group = new FormGroup({
      field: fieldControl,
      operator: operatorControl,
      value: valueControl
    });

    const condDestroy$ = new Subject<void>();
    this.conditionDestroys.push(condDestroy$);

    // When the field changes, reset the operator and value
    fieldControl.valueChanges
      .pipe(takeUntil(condDestroy$))
      .subscribe(fieldKey => {
        const seg = this.getSegmentDefinition(fieldKey);
        operatorControl.setValue(seg?.operators[0]?.value ?? '', {emitEvent: false});
        valueControl.setValue(null, {emitEvent: false});
        this.writeToControl();
      });

    // When operator changes, convert value format if needed
    operatorControl.valueChanges
      .pipe(takeUntil(condDestroy$))
      .subscribe(() => {
        valueControl.setValue(this.convertValueForOperator(group, valueControl.value), {emitEvent: false});
        this.writeToControl();
      });

    valueControl.valueChanges
      .pipe(takeUntil(condDestroy$))
      .subscribe((newValue) => {
        setTimeout(() => {
          this.writeToControl();
        }, 0);
      });

    this.conditions.push(group);
    this.writeToControl();
  }

  private getInitialValue(initial?: SegmentConditionValue): any {
    if (!initial?.field || !initial?.operator || !initial?.value) {
      return null;
    }

    const segment = this.getSegmentDefinition(initial.field);
    const inputType = segment?.operatorMetadata?.[initial.operator]?.inputType;

    // Convert primitive array to option objects for multiselect
    if (inputType === 'multiselect' && Array.isArray(initial.value)) {
      return initial.value
        .map(value => segment?.options?.find(option => option.value === value))
        .filter(option => option !== undefined);
    }

    return initial.value;
  }

  private convertValueForOperator(group: FormGroup, currentValue: any): any {
    const fieldKey = group.get('field')?.value;
    const segment = this.getSegmentDefinition(fieldKey);

    if (this.isMultiSelectOperator(group)) {
      if (!Array.isArray(currentValue)) {
        if (currentValue !== null && currentValue !== undefined) {
          const option = segment?.options?.find(opt => opt.value === currentValue);
          return option ? [option] : [];
        }
        return [];
      }
      return currentValue;
    } else {
      // Convert to single primitive value for native select
      if (Array.isArray(currentValue) && currentValue.length > 0) {
        const firstItem = currentValue[0];
        return typeof firstItem === 'object' && firstItem?.value !== undefined
          ? firstItem.value
          : firstItem;
      }
      return Array.isArray(currentValue) ? null : currentValue;
    }
  }

  removeCondition(index: number) {
    this.conditionDestroys[index].next();
    this.conditionDestroys[index].complete();
    this.conditionDestroys.splice(index, 1);
    this.conditions.splice(index, 1);
    this.writeToControl();
  }

  getSegmentDefinition(fieldKey: string): SegmentDefinition | undefined {
    return this.segments.find(segmentDefinition => segmentDefinition.field === fieldKey);
  }

  getOperatorsForGroup(group: FormGroup): { value: string; label: string }[] {
    return this.getSegmentDefinition(group.get('field')?.value)?.operators ?? [];
  }

  getOptionsForGroup(group: FormGroup): { value: any; label: string }[] {
    return this.getSegmentDefinition(group.get('field')?.value)?.options ?? [];
  }

  isMultiSelectOperator(group: FormGroup): boolean {
    const fieldKey = group.get('field')?.value;
    const operator = group.get('operator')?.value;

    if (!fieldKey || !operator) {
      return false;
    }

    const segment: SegmentDefinition = this.getSegmentDefinition(fieldKey);
    const inputType = segment?.operatorMetadata?.[operator]?.inputType;

    return inputType === 'multiselect';
  }

  private writeToControl() {
    const conditions = this.conditions
      .map(formGroup => {
        const rawCondition = formGroup.value as SegmentConditionValue;

        let processedValue = rawCondition.value;
        if (Array.isArray(processedValue) && processedValue.length > 0) {
          if (typeof processedValue[0] === 'object' && processedValue[0]?.value !== undefined) {
            processedValue = processedValue.map(item => item.value);
          }
        }

        return {
          field: rawCondition.field,
          operator: rawCondition.operator,
          value: processedValue
        };
      })
      .filter(c => !!c.field);

    const finalValue = conditions.length > 0 ? {
      logicalOperator: this.logicalOperatorControl.value,
      conditions
    } : null;

    this.control.setValue(finalValue);
    this.control.markAsDirty();
  }
}
